import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MailCheck, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

export default function VerifyEmail() {
  const { t } = useTranslation("auth");
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  // status: "loading" | "success" | "error"
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const refreshedRef = useRef(false);

  // Confirm once on mount. The token is the proof, so this must work whether
  // or not the visitor currently has a session — they may open the link in a
  // different browser than the one they signed up in.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await api.post("/auth/email/verify", { token });
        if (!cancelled) setStatus("success");
      } catch (e) {
        if (cancelled) return;
        setError(
          formatApiErrorDetail(e.response?.data?.detail) ||
            t("email_verification.error_subtitle")
        );
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Once verified, refresh the session so email_verification_status flips to
  // "ok" and the banner disappears, then route onward.
  useEffect(() => {
    if (status !== "success" || user === null) return;
    if (user && !refreshedRef.current) {
      refreshedRef.current = true;
      refresh();
    }
    const dest = user ? "/dashboard" : "/login";
    const timer = setTimeout(() => navigate(dest), 2500);
    return () => clearTimeout(timer);
  }, [status, user, refresh, navigate]);

  return (
    <div className="min-h-screen grid place-items-center px-4 py-12 bg-zinc-50" data-testid="verify-email-page">
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-2xl p-8 shadow-sm">
        <Link to="/" className="inline-block mb-6 hover:opacity-80 transition-opacity" aria-label="QENVA · The Silent Guardian">
          <img
            src="/brand/v2-primary-logo.png"
            alt="QENVA · The Silent Guardian"
            draggable={false}
            className="h-8 w-auto block select-none"
            style={{ objectFit: "contain" }}
          />
        </Link>

        <div className="w-12 h-12 rounded-xl bg-zinc-950 grid place-items-center mb-5">
          <MailCheck size={20} className="text-white" />
        </div>

        {status === "loading" && (
          <div className="text-center" data-testid="verify-email-loading">
            <Loader2 className="animate-spin mx-auto text-zinc-400" size={22} />
            <p className="text-base font-semibold text-zinc-950 mt-4">{t("email_verification.page_title")}</p>
            <p className="text-sm text-zinc-500 mt-1">{t("email_verification.loading_subtitle")}</p>
          </div>
        )}

        {status === "success" && (
          <div className="text-center" data-testid="verify-email-success">
            <CheckCircle2 className="mx-auto text-emerald-500" size={28} />
            <p className="text-base font-semibold text-zinc-950 mt-3">{t("email_verification.success_title")}</p>
            <p className="text-sm text-zinc-500 mt-1">{t("email_verification.success_subtitle")}</p>
            {user === null ? null : user ? (
              <>
                <p className="text-sm text-zinc-500 mt-3">{t("email_verification.success_loggedin_hint")}</p>
                <Link to="/dashboard" className="text-sm text-zinc-950 underline mt-4 inline-block">
                  {t("email_verification.go_to_dashboard")}
                </Link>
              </>
            ) : (
              <>
                <p className="text-sm text-zinc-500 mt-3">{t("email_verification.success_loggedout_hint")}</p>
                <Link to="/login" className="text-sm text-zinc-950 underline mt-4 inline-block">
                  {t("email_verification.go_to_login")}
                </Link>
              </>
            )}
          </div>
        )}

        {status === "error" && (
          <div className="text-center" data-testid="verify-email-error">
            <AlertCircle className="mx-auto text-rose-500" size={28} />
            <p className="text-base font-semibold text-zinc-950 mt-3">{t("email_verification.error_title")}</p>
            <p className="text-sm text-zinc-500 mt-1">{error}</p>
            <Link to="/login" className="text-sm text-zinc-950 underline mt-4 inline-block">
              {t("email_verification.back_to_login")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
